// ** React Imports
import { useState, useEffect, ChangeEvent } from 'react'

// ** Type Imports
import { DatacenterOption, WebengageAnalyticsType } from 'src/lib/types/integrations'

// ** Child Imports
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import { Switch, Grid, Typography, TextField, Checkbox } from '@mui/material'
import SaveNotification from 'src/components/common/SaveNotification'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import IntegrationChannels from 'src/components/settings/IntegrationChannels'

// ** Context Imports
import { useIntegrations } from 'src/lib/contexts/IntegrationsContext'
import { useLoader } from 'src/lib/contexts/LoaderContext'

// ** Util Imports
import { getIntegrations, setAnalyticsConfig, verifyAnalyticsConfig } from 'src/lib/core/api-methods'
import { initialWebengageAnalyticsState } from 'src/lib/utils/integrationsHelpers'
import { alertSeverites } from 'src/lib/utils/constants'
import { useTemporary } from 'src/lib/contexts/TemporaryContext'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'
import DropdownButton from 'src/components/common/form-elements/DropdownButton'
import { moengageDataCenterOptions } from 'src/assets/SettingsConfig'
import { useLocale } from 'src/lib/hooks/useLocal'
import { getEnabledChannelsArray } from 'src/lib/utils/parsers'

interface WebengageProps {
  isOnboarding?: boolean
  handleOnboardingSave?: () => void
}

const Webengage: React.FC<WebengageProps> = ({ isOnboarding = false, handleOnboardingSave }) => {
  // Context hook
  const { setLoaderProperties } = useLoader()
  const { integrations, setIntegrations } = useIntegrations()
  const { temporary, setTemporaryData } = useTemporary()
  const { t } = useLocale()
  const { webengage } = integrations || {}

  //Local State
  const [webEngageAnalytics, setWebengageAnalytics] = useState<WebengageAnalyticsType>(initialWebengageAnalyticsState)
  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [settingsUpdated, setSettingsUpdated] = useState<boolean>(false)

  useEffect(() => {
    callGetAnalytics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!temporary) return
    setWebengageAnalytics(temporary.integrations?.webengage)
    setTemporaryData(undefined)
  }, [temporary, setTemporaryData])

  useEffect(() => {
    setSettingsUpdated(Boolean(JSON.stringify(webengage) !== JSON.stringify(webEngageAnalytics)))
  }, [webengage, webEngageAnalytics])

  useEffect(() => {
    if (!webengage) return
    setWebengageAnalytics(webengage)
    setTemporaryData(undefined)
    setSettingsUpdated(false)
  }, [webengage, setTemporaryData])

  useConfirmOnRouteChanges(settingsUpdated, () => {
    return new Promise((resolve: any, reject) => {
      if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
        setTemporaryData({
          integrations: {
            ...integrations,
            webengage: webEngageAnalytics
          }
        })
        resolve(true)
      } else reject(false)
    })
  })

  const callGetAnalytics = async () => {
    try {
      const response: any = await getIntegrations()
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations(response?.data)
      }
    } catch (error) {
      handleAlerts(alertSeverites.SEVERITY_ERROR, 'Could not fetch analytics configuration!')
    }
  }

  const isValidForm = () => {
    if (
      webEngageAnalytics?.enabled &&
      (!Boolean(webEngageAnalytics?.licenseCode?.length) || !Boolean(webEngageAnalytics?.apiKey?.length))
    ) {
      setOpenAlert('error')
      setAlertMessage('Please fill required webengage analytics fields!')
      return false
    }
    return true
  }

  const callSubmit = async () => {
    if (!isValidForm()) return
    const payload = {
      enabled: webEngageAnalytics?.enabled,
      license_code: webEngageAnalytics?.licenseCode,
      api_key: webEngageAnalytics?.apiKey,
      is_india_datacenter: webEngageAnalytics?.isIndiaDatacenter,
      channels: getEnabledChannelsArray(webEngageAnalytics?.channels)
    }
    try {
      setLoaderProperties({ isLoaderVisible: true })
      const verifyResponse: any = await verifyAnalyticsConfig('webengage', payload)
      if (verifyResponse?.error) {
        setOpenAlert('error')
        setAlertMessage(verifyResponse?.error)
        console.error('x-trace-id:', verifyResponse.headers?.['x-trace-id'])
        setLoaderProperties({ isLoaderVisible: false })
        return
      }

      const response: any = await setAnalyticsConfig('webengage', payload)
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations({ ...integrations, webengage: webEngageAnalytics }, false)
        setOpenAlert('success')
        setAlertMessage('Successfully saved analytics configuration!')
        handleOnboardingSave && handleOnboardingSave()
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errorMessage
        ? error?.response?.data?.errorMessage
        : `Could not save configuration!`
      handleAlerts(alertSeverites.SEVERITY_ERROR, errorMessage)
      console.error(error)
    }
    setLoaderProperties({ isLoaderVisible: false })
  }

  const handleDiscardSaving = () => {
    if (!webengage) return
    setWebengageAnalytics(webengage)
    setSettingsUpdated(false)
    setTemporaryData(undefined)
  }

  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }

  const handleCloseAlert = (event: any, reason: any) => {
    if (reason === 'clickaway') {
      return
    }
    handleAlerts('')
  }

  return (
    <>
      <IntegrationChannels
        channels={webEngageAnalytics.channels ?? []}
        onChange={(channels: any) => {
          setWebengageAnalytics({ ...webEngageAnalytics, channels: channels })
        }}
        hideChannels={!webEngageAnalytics?.enabled}
      >
        <Grid display={'flex'} flexDirection={'column'} width={'100%'}>
          {/* <Typography variant='body1' sx={{ fontWeight: 600 }}>
                  Webengage analytics
                </Typography> */}

          <Grid item xs={12} md={12} margin={'1rem 0rem'} display={'flex'} flexDirection={'column'} gap={2}>
            <Grid display={'flex'} alignItems={'center'} gap={36}>
              <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
                API Key:
              </Typography>
              <TextField
                fullWidth
                type='text'
                size='small'
                variant='outlined'
                sx={{ maxWidth: '400px' }}
                value={webEngageAnalytics?.apiKey}
                placeholder='API Key'
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setWebengageAnalytics({ ...webEngageAnalytics, apiKey: event?.target?.value })
                }
              />
            </Grid>
            <Grid display={'flex'} alignItems={'center'} gap={26}>
              <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
                License Code:
              </Typography>
              <TextField
                fullWidth
                type='text'
                size='small'
                variant='outlined'
                sx={{ maxWidth: '400px' }}
                value={webEngageAnalytics?.licenseCode}
                inputProps={{ 'aria-label': 'controlled' }}
                placeholder='License Code'
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setWebengageAnalytics({ ...webEngageAnalytics, licenseCode: event?.target?.value })
                }
              />
            </Grid>
            <Grid display={'flex'} alignItems={'center'}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Checkbox
                  checked={webEngageAnalytics?.isIndiaDatacenter}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setWebengageAnalytics({ ...webEngageAnalytics, isIndiaDatacenter: event?.target?.checked })
                  }}
                  inputProps={{ 'aria-label': 'controlled' }}
                  sx={{ padding: 0, paddingRight: 1 }}
                />
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Typography variant='body2'>Indian Data Center</Typography>
                </div>
              </div>
            </Grid>
          </Grid>

          <Grid item xs={12} display={'flex'} alignItems={'center'}>
            <Grid container>
              <Grid item xs={10} md={11} display={'flex'} alignItems={'center'}>
                <Typography variant='body2'>Webengage analytics is</Typography>
                <Typography variant='body1' sx={{ fontWeight: 500, marginLeft: 2 }}>
                  {`${webEngageAnalytics?.enabled ? 'enabled' : 'disabled'}`}
                </Typography>
              </Grid>
              <Grid item xs={2} md={1} display={'flex'} alignItems={'center'} justifyContent={'end'}>
                <Switch
                  name='webEngageToggle'
                  checked={webEngageAnalytics?.enabled}
                  inputProps={{ 'aria-label': 'controlled' }}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setWebengageAnalytics({ ...webEngageAnalytics, enabled: event.target.checked })
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </IntegrationChannels>
      {!Boolean(isOnboarding) ? (
        <SaveNotification onSave={callSubmit} onDiscard={handleDiscardSaving} isVisible={settingsUpdated} />
      ) : (
        <button
          className='px-3 py-2 ml-auto text-sm font-bold border rounded-lg w-fit text-[#FFFFFF] bg-[#01CCA7]'
          onClick={callSubmit}
        >
          {t('save')}
        </button>
      )}
      {openAlert.length ? (
        <Alerts
          open={true}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : null}
    </>
  )
}
export default Webengage
